import Button from "@/components/bricks/button/Button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useEffect } from "react";
import { get } from "../../api/util";
import { useForm } from "../../hooks/useForm";
import {
  getInitialValues,
  getObjectDiff,
  mergeExistingValuesIntoForm,
} from "../../util/form";
import DeleteIcon from "../icons/DeleteIcon";
import Spinner from "../spinner/Spinner";
import { StickyFooter } from "../stickyFooter/modalStickyFooter";
import { BooleanFormControl } from "./BooleanFormControl";
import { FormikDatePicker } from "./DatePicker";
import HalvaarInput from "./HalvaarInput";
import { TextInputFormik } from "./InputItem";
import { FormikKodeSelect } from "./KodeSelect";
import FirmaFormControl from "./FirmaFormControl";
import PersonFormControl from "./PersonFormControl";
import { FormikTextArea } from "./TextArea";
import * as Yup from "yup";
import KursusFormControl from "./KursusFormControl";
import AddressFormControl from "./AddressFormControl";
import { AddressGenericFormSet } from "./sets/AddressGenericFormSet";
import { useConfirm } from "../confirm/useConfirm";
import RetFormControl from "./RetFormControl";
import { AlertMessage } from "../AlertMessage";
import FormikTimePicker from "./TimePicker";
import KvartalInput from "./KvartalInput";

interface IFormProps<U, V = U> {
  config: FormConfig<U, V>;
  method: FormMethod;
  setMethod?: React.Dispatch<React.SetStateAction<FormMethod>>;
  onSuccess?: () => void;
  formModalState?: {
    setShouldWarnBeforeClosing: (shouldWarn: boolean) => void;
    setIsSaving: (isLoading: boolean) => void;
    shouldWarnBeforeClosing: boolean;
    isSaving: boolean;
    open: boolean;
    isInvalidating: boolean;
  };
  setShouldReinit?: React.Dispatch<React.SetStateAction<boolean>>;
  treatAsPatch?: boolean;
}
export default function Form<U, V = U>({
  config,
  method,
  formModalState,
  onSuccess,
  setMethod,
  setShouldReinit,
  treatAsPatch,
}: IFormProps<U, V>) {
  const initialFormDataQuery =
    config.postInitialFormDataQuery && method === "post"
      ? config.postInitialFormDataQuery
      : config.get;
  const { data: initialFormData, isFetching: loadingInitialFormData } =
    useQuery({
      queryKey: ["initialFormData", initialFormDataQuery],
      queryFn: async function () {
        return get<V>({ query: initialFormDataQuery });
      },
      enabled:
        Boolean(method === "patch" || treatAsPatch) &&
        initialFormDataQuery !== "" &&
        formModalState?.open,
    });
  const queryClient = useQueryClient();
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({
        queryKey: ["initialFormData", config.get],
      });
    };
  }, [config.get, queryClient]);

  const formConfig = initialFormData
    ? mergeExistingValuesIntoForm(config, initialFormData)
    : config.form;
  const mergedConfig = { ...config, form: formConfig };

  useEffect(() => {
    if (setMethod && method === "delete") setMethod("patch");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hasPatchMethod = config.patch !== undefined;
  const hasPostMethod = config.post !== undefined;
  const {
    isError,
    isSaving: formIsSaving,
    submit,
    error,
  } = useForm(method, config, onSuccess);
  const isSaving = formIsSaving || formModalState?.isInvalidating;
  const initialValues = getInitialValues<U, V>(mergedConfig);
  const formik = useFormik({
    initialValues,
    onSubmit: (values: U & V) => {
      if (method === "patch") {
        const changedValues = getObjectDiff(initialValues, values);
        submit(changedValues);
      } else if (method === "post") {
        submit(values);
      } else {
        submit(undefined);
      }
    },
    validationSchema:
      method === "delete" ? Yup.object() : config.validationSchema,
    validate: config.validate,
    enableReinitialize: true,
  });
  const { dirty } = formik;
  useEffect(() => {
    if (formModalState && formModalState.shouldWarnBeforeClosing !== dirty) {
      formModalState.setShouldWarnBeforeClosing(dirty);
    }
  }, [dirty, formModalState]);
  useEffect(() => {
    if (formModalState && formModalState.isSaving !== formIsSaving) {
      formModalState.setIsSaving(formIsSaving);
    }
  }, [formIsSaving, formModalState]);

  const { confirmDialog } = useConfirm();

  function getErrorMessage(error, errorStatusCodeMessages) {
    if (errorStatusCodeMessages && error) {
      if (errorStatusCodeMessages[error.status]) {
        return errorStatusCodeMessages[error.status];
      }
    }
    return error?.data
      ? typeof error.data === "string"
        ? (error.data ?? "")
        : (error.data.Message ?? error.data.title ?? "")
      : typeof error === "string"
        ? error
        : "";
  }
  const errorMessage = getErrorMessage(error, config.errorStatusCodeMessages);

  return (
    <form onSubmit={formik.handleSubmit} autoComplete="off">
      <div className="c-item-list c-modal-container-inner">
        {config.form.map((formConfig) => {
          const disabled =
            isSaving || loadingInitialFormData
              ? isSaving || loadingInitialFormData
              : formControlDisabled(method, formConfig, hasPatchMethod);
          return (
            <FormControl
              disabled={disabled}
              key={formConfig.id}
              config={formConfig}
              formik={formik}
              method={method}
            />
          );
        })}
      </div>
      {isError && error && (
        <div className="c-modal-container-inner mb-2">
          <AlertMessage warningIcon type="danger">
            <p className="mb-0">{errorMessage}</p>
          </AlertMessage>
        </div>
      )}
      <StickyFooter>
        <div>
          {hasPostMethod && method === "post" && config.postReinit && (
            <Button
              type="button"
              className={isSaving ? "disabled" : ""}
              outline
              onClick={async () => {
                try {
                  await formik.submitForm();
                  if (setShouldReinit) setShouldReinit(true);
                  formik.resetForm();
                } catch (error) {
                  console.error(error);
                }
              }}
            >
              Gem og tilføj næste
            </Button>
          )}
          {config.delete !== undefined && !isSaving && method !== "post" && (
            <Button
              type="button"
              color={"secondary"}
              onClick={async () => {
                if (setMethod) {
                  setMethod("delete");
                  const confirmDelete = await confirmDialog(
                    `Bekræft sletning af ${config.title}`,
                    ``,
                    "Afbryd",
                    "Slet",
                  );
                  if (confirmDelete) {
                    submit(undefined);
                  } else {
                    setMethod("patch");
                  }
                }
              }}
            >
              <DeleteIcon height={18} /> Slet
            </Button>
          )}
        </div>

        <div className="d-flex gap-2">
          {isSaving && <Spinner className="align-self-center" size={24} />}
          {(hasPatchMethod || method !== "patch") && (
            <Button
              className={isSaving ? "disabled" : ""}
              type="submit"
              color={"primary"}
              onClick={() => {}}
            >
              Gem
            </Button>
          )}
        </div>
      </StickyFooter>
    </form>
  );
}

function FormControl<U>({
  config,
  formik,
  disabled,
  method,
}: FormikControlProps<U>) {
  const shouldHide =
    config.conditionalHidden &&
    config.conditionalHidden({ method, values: formik.values });
  switch (config.component) {
    case "adresse":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <AddressFormControl
            config={config}
            formik={formik}
            value={formik.values[config.id]}
            disabled={disabled}
          />
        </div>
      );
    case "andenAdresse":
      return shouldHide ? (
        <></>
      ) : (
        <AddressGenericFormSet formik={formik} hasCo={false} />
      );
    case "boolean":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <BooleanFormControl
            config={config}
            formik={formik}
            value={formik.values[config.id]}
            disabled={disabled}
          />
        </div>
      );
    case "custom":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          {config.customComponent({
            value: formik.values[config.id],
            error: formik.errors[config.id],
            onChange: (val) => {
              formik.setTouched({
                ...formik.touched,
                [config.id]: true,
              });
              formik.setValues({ ...formik.values, [config.id]: val }, true);
            },
            touched: formik.touched[config.id],
            formik: formik,
          })}
        </div>
      );
    case "datepicker":
      return (
        <div style={shouldHide ? { display: "none" } : {}}>
          <FormikDatePicker
            config={config}
            formik={formik}
            disabled={disabled}
          />
        </div>
      );
    case "timepicker":
      return (
        <div style={shouldHide ? { display: "none" } : {}}>
          <FormikTimePicker
            config={config}
            formik={formik}
            disabled={disabled}
          />
        </div>
      );
    case "halvaar":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <HalvaarInput config={config} formik={formik} disabled={disabled} />
        </div>
      );
    case "kvartal":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <KvartalInput config={config} formik={formik} disabled={disabled} />
        </div>
      );
    case "kode-select":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <FormikKodeSelect
            config={config}
            formik={formik}
            onChange={(kode) => {
              const value = kode
                ? ((kode.type === "enum" ? kode.id : kode.nr.toString()) ??
                  undefined)
                : "";
              formik.setTouched({
                ...formik.touched,
                [config.id]: true,
              });
              formik.setFieldValue(config.id, value, true);
            }}
            value={formik.values[config.id]?.toString()}
            disabled={disabled}
          />
        </div>
      );
    case "firma":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <FirmaFormControl
            config={config}
            formik={formik}
            value={formik.values[config.id]}
            disabled={disabled}
          />
        </div>
      );
    case "ret":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <RetFormControl
            config={config}
            formik={formik}
            value={formik.values[config.id]}
            disabled={disabled}
          />
        </div>
      );
    case "kursus":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <KursusFormControl
            config={config}
            formik={formik}
            value={formik.values[config.id]}
            disabled={disabled}
          />
        </div>
      );
    case "person":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <PersonFormControl
            config={config}
            formik={formik}
            value={formik.values[config.id]}
            disabled={disabled}
          />
        </div>
      );
    case "textarea":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <FormikTextArea config={config} formik={formik} disabled={disabled} />
        </div>
      );
    case "textinput":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          <TextInputFormik
            config={config}
            formik={formik}
            disabled={disabled}
          />
        </div>
      );
    case "customcomponent":
      return (
        <div className="flex-100" style={shouldHide ? { display: "none" } : {}}>
          {config.customComponent}
        </div>
      );
  }
}

function formControlDisabled<U>(
  method: FormMethod,
  formConfig: IEdit<U>,
  hasPatchMethod,
) {
  return (
    method === "delete" ||
    (method === "patch" && (formConfig.locked || !hasPatchMethod))
  );
}
